// Shared canonical calculations for website scheduling and gated agent discovery.
// Keep object copies CommonJS-compatible: CRA injects ESM helpers for object spread.
// Timestamp is supplied by the SDK family owning the caller; no SDK values cross families.
const { DateTime } = require("luxon");
const moment = require("moment-timezone");
const geohash = require("ngeohash");
const { decode } = geohash;
const defaultLogger = console;
const createSchedulingCore = ({ Timestamp, logger = defaultLogger } = {}) => {
  const console = logger;
  const MIN_DATE = DateTime.fromISO("0001-01-01T00:00:00.000Z");
  const MAX_DATE = DateTime.fromISO("9999-12-31T23:59:59.999Z");
function isPointInPolygon(point, polygon) {
  if (!Array.isArray(polygon) || polygon.length < 3) {
    return false;
  }

  let x, y;

  // Check if point is an array, assume [0] is lat and [1] is lng
  if (Array.isArray(point)) {
    x = point[0];
    y = point[1];
  } else {
    // If point is an object
    x = point.lat;
    y = point.lng;
  }

  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    let xi = polygon[i].lat,
      yi = polygon[i].lng;
    let xj = polygon[j].lat,
      yj = polygon[j].lng;

    let intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
}

function segmentsIntersect(p1, p2, q1, q2) {
  function ccw(a, b, c) {
    return (
      (c.lng - a.lng) * (b.lat - a.lat) > (b.lng - a.lng) * (c.lat - a.lat)
    );
  }

  return (
    ccw(p1, q1, q2) !== ccw(p2, q1, q2) && ccw(p1, p2, q1) !== ccw(p1, p2, q2)
  );
}

function doesLineExitPolygon(start, end, polygon) {
  const numPoints = polygon.length;

  for (let i = 0; i < numPoints; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % numPoints]; // wraps around

    // Ignore intersection at the endpoints
    if (
      (start.lat === a.lat && start.lng === a.lng) ||
      (end.lat === b.lat && end.lng === b.lng)
    ) {
      continue;
    }

    if (segmentsIntersect(start, end, a, b)) {
      return true; // line segment exits polygon
    }
  }

  return false; // path stays entirely within polygon
}

const findAreas = (lat, lng, areas = {}) => {
  const memberPoint = { lat, lng };
  return Object.keys(areas).filter((key) => {
    const bounds = areas?.[key]?.bounds;
    return isPointInPolygon(memberPoint, bounds);
  });
};

const getFutureAvailability = (
  employeeAvailability,
  startBoundary,
  endBoundary,
) => {
  const todayStart = DateTime.local().startOf("day");
  const MIN_DATE = DateTime.fromISO("0001-01-01T00:00:00.000Z");
  const MAX_DATE = DateTime.fromISO("9999-12-31T23:59:59.999Z");

  // Calculate total availability within given boundaries
  const totalFutureAvailability = Object.values(
    employeeAvailability?.windows || {},
  ).reduce((totalHours, window) => {
    const availabilityStart = window.start
      ? DateTime.fromJSDate(window.start.toDate())
      : MIN_DATE;
    const availabilityEnd = window.end
      ? DateTime.fromJSDate(window.end.toDate())
      : MAX_DATE;

    // Skip this window if the availability start is in the past
    if (availabilityStart < DateTime.now()) {
      return totalHours;
    }

    // Calculate the effective start and end, considering boundaries
    const effectiveStart = DateTime.max(
      availabilityStart,
      startBoundary || todayStart,
    );
    const effectiveEnd = DateTime.min(availabilityEnd, endBoundary || MAX_DATE);

    // Include this window's availability only if the effective range is valid
    if (effectiveStart < effectiveEnd) {
      const durationInHours = effectiveEnd.diff(effectiveStart, "hours").hours;
      return totalHours + durationInHours;
    }

    return totalHours;
  }, 0); // Initial total hours

  return totalFutureAvailability;
};

const getSoonestSequentialWindow = (relevantWindows) => {
  const now = DateTime.now();
  const MAX_DATE = DateTime.fromISO("9999-12-31T23:59:59.999Z");
  let soonestSequentialWindow = [];
  let currentSequence = [];

  // Iterate from the last window to the first
  for (let i = relevantWindows.length - 1; i >= 0; i--) {
    const window = relevantWindows[i];
    const previousWindow = relevantWindows[i + 1];

    const isSequential =
      currentSequence.length === 0 ||
      (previousWindow &&
        (!window.endDate || // Open-ended windows are sequential with any later window
          window.endDate.plus({ days: 1 }) >= previousWindow.startDate));

    if (isSequential) {
      currentSequence.unshift(window);
    } else {
      // Validate the current sequence
      const sequenceEnd =
        currentSequence[currentSequence.length - 1]?.endDate || MAX_DATE;
      if (sequenceEnd >= now) {
        soonestSequentialWindow = [...currentSequence];
      }
      currentSequence = [window];
    }
  }

  // Check the last sequence after the loop
  const sequenceEnd =
    currentSequence[currentSequence.length - 1]?.endDate || MAX_DATE;
  if (sequenceEnd >= now) {
    soonestSequentialWindow = [...currentSequence];
  }

  return soonestSequentialWindow;
};

const getCurrentArea = (areas = {}, now = DateTime.now()) => {
  const entries = Object.entries(areas || {});
  const hasAnyWindows = entries.some(
    ([, areaData]) => Object.keys(areaData?.windows || {}).length > 0,
  );

  // Iterate through the areas to find the area that contains the point
  for (const [areaKey, areaData] of entries) {
    // Convert windows object map to an array of window objects
    const windowsArray = Object.values(areaData.windows || {});

    // Check windows for the current area
    const currentWindow = windowsArray.find((window) => {
      const windowStart = window.start
        ? DateTime.fromJSDate(window.start.toDate())
        : null;
      const windowEnd = window.end
        ? DateTime.fromJSDate(window.end.toDate())
        : null;

      // Determine if the current time falls within the window
      return (
        (!windowStart || windowStart <= now) && // Start is in the past or now
        (!windowEnd || windowEnd >= now) // End is in the future or now
      );
    });

    // If a matching window is found, return the area with the window
    if (currentWindow) {
      return {
        areaKey, // Return the areaKey along with the areaData
        areaData,
      };
    }
  }

  // Fallback only for single-area helpers with no timeline windows at all.
  const fallback = entries[0];
  return entries.length === 1 && !hasAnyWindows && fallback
    ? { areaKey: fallback[0], areaData: fallback[1] }
    : null;
};

const buildCoverageAreasFromPublicTimeline = ({
  currentArea = null,
  timeline = {},
} = {}) => {
  const normalizedAreaKey = String(currentArea?.key || "").trim();
  if (!normalizedAreaKey || !currentArea) return {};

  const timelineEntries = (
    Array.isArray(timeline?.entries)
      ? timeline.entries
      : Object.entries(timeline || {}).map(([entryId, entry]) =>
          Object.assign({}, { id: entryId }, entry),
        )
  )
    .map((entry, index) => ({
      entryId: String(entry?.entryId || entry?.id || `entry_${index}`).trim(),
      areaKey: String(entry?.areaKey || "").trim(),
      start: entry?.start || null,
      end: entry?.end || null,
      weeklyAvailability: Number(entry?.weeklyAvailability || 0),
    }))
    .filter(
      (entry) =>
        entry.entryId && entry.areaKey === normalizedAreaKey && entry.start,
    );

  const windows = timelineEntries.length
    ? Object.fromEntries(
        timelineEntries.map((entry) => [
          entry.entryId,
          {
            start: entry.start,
            end: entry.end || null,
            weeklyAvailability: Number(entry?.weeklyAvailability || 0),
          },
        ]),
      )
    : currentArea?.start || currentArea?.end
      ? {
          active: {
            start: currentArea?.start || null,
            end: currentArea?.end || null,
            weeklyAvailability: Number(currentArea?.weeklyAvailability || 0),
          },
        }
      : {};

  return {
    [normalizedAreaKey]: Object.assign({}, currentArea, { windows }),
  };
};

const getEmployeeCoverageAreas = (employeeData = {}) =>
  buildCoverageAreasFromPublicTimeline({
    currentArea: employeeData?.currentArea || null,
    timeline: employeeData?.timeline || {},
  });

const getCoverageWindowsForPoint = (lat, lng, areas = {}) => {
  const areaKeys = findAreas(lat, lng, areas);
  const relevantWindows = [];

  areaKeys.forEach((key) => {
    const area = areas[key];
    Object.entries(area?.windows || {}).forEach(([windowKey, window]) => {
      const startDate = window?.start
        ? DateTime.fromJSDate(window.start.toDate())
        : MIN_DATE;
      const endDate = window?.end
        ? DateTime.fromJSDate(window.end.toDate())
        : null;

      relevantWindows.push({
        key: windowKey,
        areaKey: key,
        startDate,
        endDate,
      });
    });
  });

  relevantWindows.sort((a, b) => a.startDate - b.startDate);
  return relevantWindows;
};

const shouldIncludeHelperInMemberList = ({ inArea = false } = {}) =>
  Boolean(inArea);

const getEmployeeStatus = (
  lat,
  lng,
  areas = {},
  availability,
  totalAvailableHours = null,
) => {
  const areaKeys = findAreas(lat, lng, areas);
  const relevantWindows = getCoverageWindowsForPoint(lat, lng, areas);
  const fallbackCoverageWindows =
    areaKeys.length > 0 &&
    Object.keys(areas || {}).length === 1 &&
    relevantWindows.length === 0
      ? [
          {
            key: "fallback",
            areaKey: areaKeys[0],
            startDate: MIN_DATE,
            endDate: null,
          },
        ]
      : [];
  const effectiveCoverageWindows =
    relevantWindows.length > 0 ? relevantWindows : fallbackCoverageWindows;

  if (Object.keys(areas).length === 0)
    return {
      inArea: false,
      coverageWindows: [],
      futureAvailability: 0,
      start: undefined,
      end: undefined,
    };

  const soonestWindow = getSoonestSequentialWindow(effectiveCoverageWindows);

  // Step 3: Determine availability
  const activeSequence = soonestWindow.flat();

  const fallbackStart = DateTime.now().minus({ months: 1 }); // Last month
  // const fallbackEnd = DateTime.now().plus({ years: 1 }); // A year from now

  let start, end;

  console.log("getEmployeeStatus - activeSequence: ", activeSequence);

  if (activeSequence.length > 0) {
    start = activeSequence[0].startDate;
    end = activeSequence[activeSequence.length - 1].endDate || null;
  }
  // There is no active sequence, meaning no windows
  else {
    start = undefined;
    end = undefined;
  }

  // If only one area and no windows, override start and end**
  if (
    Object.keys(areas).length === 1 &&
    activeSequence.length === 0 &&
    relevantWindows.length === 0
  ) {
    start = fallbackStart;
    end = null; // null represents indefinite future
    console.log("getEmployeeStatus - setting fallback");
  }

  let futureAvailability;

  // If we're within the helpers provider, we can do exact availability
  if (totalAvailableHours !== null) {
    futureAvailability = totalAvailableHours;
  }

  // Else we use an approximate
  else {
    futureAvailability =
      effectiveCoverageWindows.length > 0
        ? getFutureAvailabilityForCoverageWindows(
            availability,
            effectiveCoverageWindows,
          )
        : getFutureAvailability(availability, start, end);
  }

  console.log("getEmployeeStatus - end: ", end);

  return {
    inArea: areaKeys.length > 0,
    coverageWindows: effectiveCoverageWindows,
    futureAvailability,
    start,
    end,
  };
};

const getFutureAvailabilityForCoverageWindows = (
  availability = {},
  coverageWindows = [],
) =>
  Object.values(availability?.windows || {}).reduce((totalHours, window) => {
    const availabilityStart = window?.start
      ? DateTime.fromJSDate(window.start.toDate())
      : MIN_DATE;
    const availabilityEnd = window?.end
      ? DateTime.fromJSDate(window.end.toDate())
      : MAX_DATE;

    if (availabilityEnd <= DateTime.now()) {
      return totalHours;
    }

    const effectiveAvailabilityStart = DateTime.max(
      availabilityStart,
      DateTime.now(),
    );

    const overlappedHours = coverageWindows.reduce((sum, coverageWindow) => {
      const coverageStart = coverageWindow?.startDate || MIN_DATE;
      const coverageEnd = coverageWindow?.endDate || MAX_DATE;
      const effectiveStart = DateTime.max(
        effectiveAvailabilityStart,
        coverageStart,
      );
      const effectiveEnd = DateTime.min(availabilityEnd, coverageEnd);

      if (effectiveStart < effectiveEnd) {
        return sum + effectiveEnd.diff(effectiveStart, "hours").hours;
      }

      return sum;
    }, 0);

    return totalHours + overlappedHours;
  }, 0);

const filterAvailabilityWindowsByCoverage = ({
  availability = {},
  coverageWindows = [],
} = {}) => {
  if (!coverageWindows.length) return {};

  const filteredWindows = {};

  Object.entries(availability?.windows || {}).forEach(([windowId, window]) => {
    const availabilityStart = window?.start
      ? DateTime.fromJSDate(window.start.toDate())
      : null;
    const availabilityEnd = window?.end
      ? DateTime.fromJSDate(window.end.toDate())
      : null;

    if (!availabilityStart || !availabilityEnd) return;

    coverageWindows.forEach((coverageWindow, index) => {
      const coverageStart = coverageWindow?.startDate || MIN_DATE;
      const coverageEnd = coverageWindow?.endDate || MAX_DATE;
      const effectiveStart = DateTime.max(availabilityStart, coverageStart);
      const effectiveEnd = DateTime.min(availabilityEnd, coverageEnd);

      if (effectiveStart < effectiveEnd) {
        filteredWindows[`${windowId}_${index}`] = Object.assign({}, window, {
          start: Timestamp.fromDate(effectiveStart.toJSDate()),
          end: Timestamp.fromDate(effectiveEnd.toJSDate()),
        });
      }
    });
  });

  return filteredWindows;
};

const isRangeWithinCoverageWindows = ({
  start = null,
  end = null,
  coverageWindows = [],
} = {}) => {
  const rangeStart =
    typeof start?.toDate === "function"
      ? DateTime.fromJSDate(start.toDate())
      : start instanceof Date
        ? DateTime.fromJSDate(start)
        : DateTime.isDateTime(start)
          ? start
          : null;
  const rangeEnd =
    typeof end?.toDate === "function"
      ? DateTime.fromJSDate(end.toDate())
      : end instanceof Date
        ? DateTime.fromJSDate(end)
        : DateTime.isDateTime(end)
          ? end
          : null;

  if (!rangeStart || !rangeEnd) return false;

  return coverageWindows.some((coverageWindow) => {
    const coverageStart = coverageWindow?.startDate || MIN_DATE;
    const coverageEnd = coverageWindow?.endDate || MAX_DATE;
    return coverageStart <= rangeStart && rangeEnd <= coverageEnd;
  });
};

const distanceBetweenGeohashes = (geohash1, geohash2) => {
  console.log("geohash1: ", geohash1);
  console.log("geohash2: ", geohash2);

  // Decode geohashes to latitude and longitude
  const { latitude: lat1, longitude: lon1 } = geohash.decode(geohash1);
  const { latitude: lat2, longitude: lon2 } = geohash.decode(geohash2);

  // Convert degrees to radians
  const toRadians = (degrees) => (degrees * Math.PI) / 180;

  const lat1Rad = toRadians(lat1);
  const lon1Rad = toRadians(lon1);
  const lat2Rad = toRadians(lat2);
  const lon2Rad = toRadians(lon2);

  // Haversine formula
  const deltaLat = lat2Rad - lat1Rad;
  const deltaLon = lon2Rad - lon1Rad;
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // Distance in miles
  const EARTH_RADIUS_MILES = 3958.8;
  return EARTH_RADIUS_MILES * c;
};

const isWindowWithinDrivingRadius = ({
  window = {},
  memberGeohash = "",
  limitDrivingRadiusMiles = null,
  areas = {},
  serviceAreaBypass = false,
} = {}) => {
  if (!limitDrivingRadiusMiles) return true;
  const adjacentGeohashes = [window?.startGeohash6, window?.endGeohash6].filter(
    Boolean,
  );
  if (!adjacentGeohashes.length) return true;
  if (!memberGeohash) return false;
  const { areaData = {} } = getCurrentArea(areas || []) || {};
  const { bounds = [] } = areaData || {};
  const decodeGeohash = (value) => {
    const { latitude, longitude } = decode(value);
    return { lat: latitude, lng: longitude };
  };
  const memberPoint = decodeGeohash(memberGeohash);

  return adjacentGeohashes.every((adjacentGeohash) => {
    const distance = distanceBetweenGeohashes(adjacentGeohash, memberGeohash);
    if (distance > limitDrivingRadiusMiles) return false;
    if (serviceAreaBypass) return true;
    return !doesLineExitPolygon(
      decodeGeohash(adjacentGeohash),
      memberPoint,
      bounds,
    );
  });
};

const calculateVisits = async ({
  employees,
  timeZoneId,
  memberGeohash,
  durations = [],
  memberId,
}) => {
  console.time("-------------------- Execution Time");

  // console.log("memberGeohash: ", memberGeohash);

  // console.log("==> Calculate Visits employees: ", employees);
  // console.log("==> Calculate Visits timeZoneId: ", timeZoneId);

  // employees must have key as id and property "windows"
  // employees also can have a startDate and endDate (luxon) that says when they are available for this time.

  const HOUR_TO_MILLIS = 60 * 60 * 1000;
  const TRAVEL_TIME_MILLIS = 30 * 60 * 1000; // 30 minutes in milliseconds
  const visits = []; // Initialize your visits variable

  // Function to adjust an employee's windows based on their booking constraints
  const adjustWindowsBasedOnBookingConstraints = (
    windows,
    bookingDaysInAdvance,
    isAvailableToday,
    limitDrivingRadiusMiles,
    memberGeohash,
    startDate, // the sequential area window start
    endDate, // the sequential area window end
    memberId,
    areas,
    coverageWindows = [],
    serviceAreaBypass = false,
  ) => {
    if (windows && windows.length === 0) {
      // console.log("EMPTY");
      return [];
    }

    const now = moment().tz(timeZoneId);
    const oneHourFromNow = moment().tz(timeZoneId).add(1, "hours");

    // Calculate how many minutes to add to round up to the next quarter-hour
    const minutes = 15 - (oneHourFromNow.minutes() % 15);
    if (minutes !== 15) {
      // This check prevents adding 15 minutes when already at the quarter-hour
      oneHourFromNow.add(minutes, "minutes");
    }

    return windows
      .map((window) => {
        let windowStart = moment(window.start.toMillis()).tz(timeZoneId);
        let windowEnd = moment(window.end.toMillis()).tz(timeZoneId);

        const startGeohash6 = window.startGeohash6 || null;
        const endGeohash6 = window.endGeohash6 || null;

        // Show only / hide from - individual list filter ----------------------------- //

        const showOnlyList = window.showOnly || [];
        const hideFromList = window.hideFrom || [];

        // console.log("showOnlyList: ", showOnlyList);
        // console.log("hideFromList: ", hideFromList);

        if (memberId) {
          if (showOnlyList.length > 0) {
            const isAllowed = showOnlyList.some((u) => u.id === memberId);
            console.log("memberId: ", memberId);
            console.log("isAllowed: ", isAllowed);
            if (!isAllowed) return null;
          }

          if (hideFromList.length > 0) {
            const isHidden = hideFromList.some((u) => u.id === memberId);
            console.log("memberId: ", memberId);
            console.log("isHidden: ", isHidden);
            if (isHidden) return null;
          }
        }

        // Adjust windowStart and windowEnd based on employee's startDate and endDate
        // if (
        //   typeof startDate === "undefined" &&
        //   typeof endDate === "undefined"
        // ) {
        //   // Exit early if both startDate and endDate are strictly undefined
        //   return null;
        // }
        // console.log("limitDrivingRadiusMiles: ", limitDrivingRadiusMiles);

        if (
          !isWindowWithinDrivingRadius({
            window: { startGeohash6, endGeohash6 },
            memberGeohash,
            limitDrivingRadiusMiles,
            areas,
            serviceAreaBypass,
          })
        ) {
          return null;
        }

        // Adjust windowStart and windowEnd based on employee's startDate and endDate
        if (!coverageWindows.length && startDate) {
          const employeeStart = moment(startDate.toMillis()).tz(timeZoneId);
          if (employeeStart.isAfter(windowEnd)) {
            // The window ends before the employee starts, discard it
            return null;
          } else if (employeeStart.isAfter(windowStart)) {
            // Adjust windowStart to employeeStart
            windowStart = employeeStart.clone();
          }
        }

        if (!coverageWindows.length && endDate) {
          const employeeEnd = moment(endDate.toMillis()).tz(timeZoneId);
          if (employeeEnd.isBefore(windowStart)) {
            // The window starts after the employee ends, discard it
            return null;
          } else if (employeeEnd.isBefore(windowEnd)) {
            // Adjust windowEnd to employeeEnd
            windowEnd = employeeEnd.clone();
          }
        }

        // Create a separate variable for the start of the day
        const windowStartDay = windowStart.clone().startOf("day");
        const daysFromToday = windowStartDay.diff(
          now.clone().startOf("day"),
          "days",
        );

        // If 'isAvailableToday' is off and it's today, discard the window.
        if (!isAvailableToday && daysFromToday === 0) {
          return null;
        }

        // If 'isAvailableToday' is on, it's today, and the window is relevant (i.e., hasn't ended yet),
        if (
          isAvailableToday &&
          daysFromToday === 0 &&
          oneHourFromNow.isBefore(windowEnd)
        ) {
          if (oneHourFromNow.isAfter(windowStart)) {
            return Object.assign({}, window, {
              start: { toMillis: () => oneHourFromNow.valueOf() },
            });
          } else {
            return window;
          }
        }

        // If the window is before the 'bookingDaysInAdvance', discard it.
        if (daysFromToday < bookingDaysInAdvance) {
          return null;
        }

        // For any other scenarios, the window remains valid and unmodified.
        return window;
      })
      .filter(Boolean); // Filter out the null entries after processing.
  };

  // Preprocess the employees to adjust their windows based on booking constraints.
  const adjustedEmployees = Object.fromEntries(
    Object.entries(employees).map(([id, employee]) => {
      const adjustedWindows = adjustWindowsBasedOnBookingConstraints(
        employee.availableWindows,
        employee.bookingDaysInAdvance,
        employee.isAvailableToday,
        employee.limitDrivingRadiusMiles,
        memberGeohash,
        employee.startDate,
        employee.endDate,
        memberId,
        employee.coverageAreas || getEmployeeCoverageAreas(employee),
        employee.coverageWindows || [],
        employee.serviceAreaBypass === true,
      );
      return [id, Object.assign({}, employee, { windows: adjustedWindows })];
    }),
  );

  const getEmployeeCombos = (employees) => {
    const employeeIds = Object.keys(employees);
    const totalEmployees = employeeIds.length;

    const generateCombinations = (arr, comboSize) => {
      // generate all combinations of a given size (comboSize) from an input array (arr).
      if (comboSize === 0) return [[]];
      if (arr.length === 0) return [];

      const head = arr[0];
      const withoutHead = generateCombinations(arr.slice(1), comboSize);
      const withHead = generateCombinations(arr.slice(1), comboSize - 1).map(
        (c) => [head, ...c],
      );

      return [...withoutHead, ...withHead];
    };

    const getComboWindows = (combo, employees) => {
      const getIntersectionOfWindows = (windowA, windowB) => {
        const start = Math.max(
          windowA.start.toMillis(),
          windowB.start.toMillis(),
        );
        const end = Math.min(windowA.end.toMillis(), windowB.end.toMillis());

        if (start < end) {
          // Determine Geohash6 values to include
          const startGeohash6 =
            windowA.startGeohash6 || windowB.startGeohash6 || null;
          const endGeohash6 =
            windowA.endGeohash6 || windowB.endGeohash6 || null;
          const sequentialVisits =
            windowA.sequentialVisits || windowB.sequentialVisits || null;

          return Object.assign(
            {},
            {
              start: { toMillis: () => start },
              end: { toMillis: () => end },
            },
            startGeohash6 && { startGeohash6 },
            endGeohash6 && { endGeohash6 },
            sequentialVisits && { sequentialVisits },
          );
        } else {
          return null;
        }
      };

      let overlaps = employees[combo[0]].windows;

      for (let i = 1; i < combo.length; i++) {
        const employeeWindows = employees[combo[i]].windows;

        let newOverlaps = [];

        for (let overlap of overlaps) {
          for (let window of employeeWindows) {
            const intersection = getIntersectionOfWindows(overlap, window);
            if (intersection) {
              newOverlaps.push(intersection);
            }
          }
        }

        overlaps = newOverlaps;
      }

      return overlaps;
    };

    const results = {};

    // Main loop to generate combinations and find overlaps
    for (let comboSize = 1; comboSize <= totalEmployees; comboSize++) {
      const combos = generateCombinations(employeeIds, comboSize);

      combos.forEach((combo) => {
        const windows = getComboWindows(combo, employees);
        if (windows && windows.length > 0) {
          // join all employeeId's into a dashed string and set windows as property
          results[combo.join("-")] = { windows };
        }
      });
    }

    return results;
  };

  function calculateFrequencyForVisit(visit, employeeWindows) {
    // Helper function to check if a visit (shifted by 'weeksToAdd') falls within any of the employee's windows

    function fallsWithinFutureWindow(visit, employeeWindows) {
      const frequencies = [1, 2, 3, 4];

      // Calculate the shifted visits (1 to 4 weeks ahead)
      const futureVisits = frequencies.map((weeks) => {
        return {
          start: moment
            .tz(visit.start, "America/Los_Angeles")
            .add(weeks, "weeks")
            .valueOf(),
          end: moment
            .tz(visit.end, "America/Los_Angeles")
            .add(weeks, "weeks")
            .valueOf(),
        };
      });

      const checks = futureVisits.map((futureVisit) => {
        return employeeWindows.some((window) => {
          return (
            futureVisit.start >= window.start.toMillis() &&
            futureVisit.end <= window.end.toMillis()
          );
        });
      });

      return checks;
    }

    const checks = fallsWithinFutureWindow(visit, employeeWindows);

    if (checks.every(Boolean)) {
      return [0, 1, 2, 4];
    }
    if (checks[1] && checks[3]) {
      return [0, 2, 4];
    }
    if (checks[3]) {
      return [0, 4];
    }
    return [0];
  }

  function calculateLongestDurationRounded(employeeWindows) {
    // Extract the durations of all windows for the current employee/combo
    const durations = employeeWindows.map(
      (window) => window.end.toMillis() - window.start.toMillis(),
    );

    // Find the longest duration in milliseconds
    const maxDurationMillis = Math.max(...durations);

    // Convert the longest duration to hours
    const longestDuration = maxDurationMillis / HOUR_TO_MILLIS;

    // Round down the duration to get full hours
    const roundedLongestDuration = Math.floor(longestDuration);

    return roundedLongestDuration;
  }

  const employeeCombos = getEmployeeCombos(adjustedEmployees);

  // Loop through each employee (or employee combo)
  Object.keys(employeeCombos).forEach((employeeKey) => {
    const employeeWindows = employeeCombos[employeeKey].windows;
    const roundedLongestDuration =
      calculateLongestDurationRounded(employeeWindows);

    /*
    // Calculate and log available visits for each duration
    employeeWindows.forEach((window) => {
      const endMillis = window.end.toMillis();
      for (
        let durationHours = 1;
        durationHours <= roundedLongestDuration;
        durationHours++
      ) {
        const visitDurationMillis = durationHours * HOUR_TO_MILLIS;
        const totalIntervalMillis = visitDurationMillis + TRAVEL_TIME_MILLIS;

        let startMillis = window.start.toMillis();

        while (startMillis + visitDurationMillis <= endMillis) {
          const frequency = calculateFrequencyForVisit(
            { start: startMillis, end: startMillis + visitDurationMillis },
            employeeWindows
          );
          visits.push({
            employeeIds: employeeKey.split("-"),
            start: startMillis,
            end: startMillis + visitDurationMillis,
            frequency: frequency,
            duration: durationHours, // Added the roundedLongestDuration to the visit object
          });
          startMillis += totalIntervalMillis; // Move to the next possible visit start
        }
      }
    });
    */

    employeeWindows.forEach((window) => {
      const endMillis = window.end.toMillis();
      const startMillisInitial = window.start.toMillis();
      const hasStartGeohash = !!window.startGeohash6;
      const hasEndGeohash = !!window.endGeohash6;

      for (
        let durationHours = 1;
        durationHours <= roundedLongestDuration;
        durationHours++
      ) {
        const visitDurationMillis = durationHours * HOUR_TO_MILLIS;
        const totalIntervalMillis = visitDurationMillis + TRAVEL_TIME_MILLIS;

        if (hasStartGeohash || (!hasStartGeohash && !hasEndGeohash)) {
          // Work forward
          let startMillis = startMillisInitial;
          while (startMillis + visitDurationMillis <= endMillis) {
            const frequency = calculateFrequencyForVisit(
              { start: startMillis, end: startMillis + visitDurationMillis },
              employeeWindows,
            );
            visits.push({
              employeeIds: employeeKey.split("-"),
              start: startMillis,
              end: startMillis + visitDurationMillis,
              startDate: DateTime.fromMillis(startMillis, { zone: timeZoneId }),
              endDate: DateTime.fromMillis(startMillis + visitDurationMillis, {
                zone: timeZoneId,
              }),
              frequency: frequency,
              duration: durationHours,
            });

            // Exit the loop if sequentialVisits is true
            if (window.sequentialVisits && (hasStartGeohash || hasEndGeohash))
              break;
            startMillis += totalIntervalMillis; // Move forward
          }
        } else if (hasEndGeohash) {
          // Work backward
          let startMillis = endMillis - visitDurationMillis;
          while (startMillis >= startMillisInitial) {
            const frequency = calculateFrequencyForVisit(
              { start: startMillis, end: startMillis + visitDurationMillis },
              employeeWindows,
            );
            visits.push({
              employeeIds: employeeKey.split("-"),
              start: startMillis,
              end: startMillis + visitDurationMillis,
              startDate: DateTime.fromMillis(startMillis, { zone: timeZoneId }),
              endDate: DateTime.fromMillis(startMillis + visitDurationMillis, {
                zone: timeZoneId,
              }),
              frequency: frequency,
              duration: durationHours,
            });

            // Exit the loop if sequentialVisits is true
            if (window.sequentialVisits && (hasStartGeohash || hasEndGeohash))
              break;
            startMillis -= totalIntervalMillis; // Move backward
          }
        }
      }
    });
  });

  // Log the visits
  /*
  console.log("All available visits:");
  visits.forEach((visit) => {
    const startTime = moment(visit.start)
      .tz("America/Los_Angeles")
      .format("YYYY-MM-DD hh:mm:ss A");
    const endTime = moment(visit.end)
      .tz("America/Los_Angeles")
      .format("YYYY-MM-DD hh:mm:ss A");
    console.log(
      `Employees ${visit.employeeIds.join(
        ", "
      )}: Start - ${startTime}, End - ${endTime}, Duration - ${visit.duration}, frequency: ${visit.frequency.join(
        ", "
      )}`
    );
  });
  */

  console.timeEnd("-------------------- Execution Time");

  return visits;
};

function subtractWindowsFromVisits(
  windows,
  visits,
  sequentialVisits,
  limitDrivingRadiusMiles,
  timeZoneId = "America/Los_Angeles",
) {
  const adjustedWindows = [];
  let visitIndex = 0;
  let windowIndex = 0;

  // Convert objects to arrays and sort them
  const windowsArray = Object.values(windows).sort(
    (a, b) => a.start.toMillis() - b.start.toMillis(),
  );
  const visitsArray = Object.values(visits).sort(
    (a, b) => a.start.toMillis() - b.start.toMillis(),
  );

  // console.log("Windows:");
  // windowsArray.forEach((window, index) => {
  //   console.log(`Window ${index + 1}`);
  //   console.log(`  Start: ${convertTimestampToDateString(window.start)}`);
  //   console.log(`  End: ${convertTimestampToDateString(window.end)}`);
  // });

  // console.log("Visits:");
  // visitsArray.forEach((visit, index) => {
  //   console.log(`Visit ${index + 1}`);
  //   console.log(`  Start: ${convertTimestampToDateString(visit.start)}`);
  //   console.log(`  End: ${convertTimestampToDateString(visit.end)}`);
  // });

  while (windowIndex < windowsArray.length) {
    const window = windowsArray[windowIndex];

    if (!window.hasOwnProperty("sequentialVisits")) {
      window.sequentialVisits = sequentialVisits; // Replace with the desired default value
    }

    if (!window.hasOwnProperty("limitDrivingRadiusMiles")) {
      window.limitDrivingRadiusMiles = limitDrivingRadiusMiles; // Replace with the desired default value
    }

    if (visitIndex < visitsArray.length) {
      const visit = visitsArray[visitIndex];

      if (visit.end.toMillis() <= window.start.toMillis()) {
        // Visit ends before window starts, move to next visit
        visitIndex++;
      } else if (visit.start.toMillis() >= window.end.toMillis()) {
        // Visit starts after window ends, add whole window to adjustedWindows
        adjustedWindows.push(window);
        windowIndex++;
      } else {
        // Visit is within the window. Split the window.
        if (window.start.toMillis() < visit.start.toMillis()) {
          adjustedWindows.push(Object.assign({}, window, {
            start: window.start,
            end: visit.start,
            endGeohash6: visit.geohash6,
          }));
        }
        // Set the start of the window to the end of the visit for the next loop iteration
        // If the adjusted window has zero length or is negative, skip to the next window
        if (window.end.toMillis() > visit.end.toMillis()) {
          window.startGeohash6 = visit.geohash6;
          window.start = visit.end;
          visitIndex++;
        } else {
          windowIndex++;
        }
      }
    } else {
      // No more visitsArray to check against, just add the remaining windowsArray
      adjustedWindows.push(window);
      windowIndex++;
    }
  }

  // console.log("adjusted windows:");
  // adjustedWindows.forEach((window) => {
  //   console.log("-------");
  //   console.log("Start:", window.start.toDate().toLocaleString());
  //   console.log("End:", window.end.toDate().toLocaleString());
  //   console.log("startGeohash6:", window.startGeohash6);
  //   console.log("endGeohash6:", window.endGeohash6);
  // });

  if (!(Number(limitDrivingRadiusMiles) > 0)) return adjustedWindows;

  const isSameCalendarDay = (leftMillis, rightMillis) =>
    DateTime.fromMillis(leftMillis, { zone: timeZoneId }).toISODate() ===
    DateTime.fromMillis(rightMillis, { zone: timeZoneId }).toISODate();

  return adjustedWindows.map((window) => {
    const windowStart = window.start.toMillis();
    const windowEnd = window.end.toMillis();
    let nearestPrevious = null;
    let nearestNext = null;

    visitsArray.forEach((visit) => {
      if (
        visit.end.toMillis() <= windowStart &&
        isSameCalendarDay(visit.end.toMillis(), windowStart) &&
        (!nearestPrevious ||
          visit.end.toMillis() > nearestPrevious.end.toMillis())
      ) {
        nearestPrevious = visit;
      }
      if (
        visit.start.toMillis() >= windowEnd &&
        isSameCalendarDay(visit.start.toMillis(), windowEnd) &&
        (!nearestNext || visit.start.toMillis() < nearestNext.start.toMillis())
      ) {
        nearestNext = visit;
      }
    });

    return Object.assign(
      {},
      window,
      !window.startGeohash6 && nearestPrevious?.geohash6
        ? { startGeohash6: nearestPrevious.geohash6 }
        : {},
      !window.endGeohash6 && nearestNext?.geohash6
        ? { endGeohash6: nearestNext.geohash6 }
        : {},
    );
  });
}

function padVisits(visits = {}, paddingMinutes, currentMemberId = null) {
  return Object.entries(visits).reduce((acc, [id, visit]) => {
    if (!visit?.start || !visit?.end) {
      return acc;
    }

    const isSameMember = currentMemberId && visit.member === currentMemberId;

    const startMillis = visit.start.toDate().getTime();
    const endMillis = visit.end.toDate().getTime();

    acc[id] = {
      start: new Date(
        isSameMember ? startMillis : startMillis - paddingMinutes * 60 * 1000,
      ),
      end: new Date(
        isSameMember ? endMillis : endMillis + paddingMinutes * 60 * 1000,
      ),
      geohash6: visit.location?.geohash || "",
    };

    // Convert back to Firebase Timestamp
    acc[id].start = Timestamp.fromMillis(acc[id].start);
    acc[id].end = Timestamp.fromMillis(acc[id].end);

    return acc;
  }, {});
}

const computeAvailabilityData = ({ employeesData, visitsData, activeEmployeeIds = Object.keys(employeesData).slice(0, 20), memberId, timeZoneId = "America/Los_Angeles" }) => {
      const updatedAvailabilityData = {};

      activeEmployeeIds.forEach((employeeId) => {
        const employeeData = employeesData[employeeId];
        const employeeVisits = visitsData[employeeId]; // Get visits for this employee
        if (!employeeData.availability || !employeeVisits) return; // Skip if data is incomplete

        const { suspendedUntil = null } = employeeData;

        if (
          suspendedUntil &&
          DateTime.fromJSDate(suspendedUntil.toDate()) > DateTime.now()
        ) {
          return; // Skip this employee if suspended
        }

        const {
          bookingDaysInAdvance = 0, // default to 0
          isAvailableToday,
          sequentialVisits,
          limitDrivingRadiusMiles,
          serviceAreaBypass = false,
          avatarUrl,
          firstName,
          lastName,
          startDate = null,
          endDate = null,
          areas = {},
          coverageWindows = [],
          availability: { windows = {} } = {},
        } = employeeData;

        console.log("HelpersProvider - windows: ", windows);

        // Convert availability windows to Firestore Timestamps
        const tempWindows = Object.entries(windows).reduce(
          (acc, [key, window]) => {
            acc[key] = Object.assign({}, window, {
              start: new Timestamp(
                window.start.seconds,
                window.start.nanoseconds,
              ),
              end: new Timestamp(window.end.seconds, window.end.nanoseconds),
            });
            return acc;
          },
          {},
        );
        const filteredWindows = serviceAreaBypass
          ? tempWindows
          : filterAvailabilityWindowsByCoverage({
              availability: {
                windows: tempWindows,
              },
              coverageWindows,
            });
        const effectiveDrivingRadiusMiles = serviceAreaBypass
          ? Number.isFinite(Number(limitDrivingRadiusMiles)) &&
            Number(limitDrivingRadiusMiles) > 0
            ? Number(limitDrivingRadiusMiles)
            : 15
          : limitDrivingRadiusMiles;

        // Pad visits and calculate available windows
        const paddedVisits = padVisits(employeeVisits, 30, memberId); // Pad visits
        const availableWindows = subtractWindowsFromVisits(
          filteredWindows,
          paddedVisits,
          sequentialVisits,
          effectiveDrivingRadiusMiles,
          timeZoneId,
        );

        // Add to updated availability data
        updatedAvailabilityData[employeeId] = {
          availableWindows,
          bookingDaysInAdvance,
          sequentialVisits,
          limitDrivingRadiusMiles: effectiveDrivingRadiusMiles,
          serviceAreaBypass,
          isAvailableToday,
          avatarUrl,
          firstName,
          lastName,
          startDate,
          endDate,
          areas,
        };
      });

      return updatedAvailabilityData;
    };
  return { isPointInPolygon, segmentsIntersect, doesLineExitPolygon, findAreas, getFutureAvailability, getSoonestSequentialWindow, getCurrentArea, buildCoverageAreasFromPublicTimeline, getEmployeeCoverageAreas, getCoverageWindowsForPoint, shouldIncludeHelperInMemberList, getEmployeeStatus, getFutureAvailabilityForCoverageWindows, filterAvailabilityWindowsByCoverage, isRangeWithinCoverageWindows, distanceBetweenGeohashes, isWindowWithinDrivingRadius, calculateVisits, subtractWindowsFromVisits, padVisits, computeAvailabilityData };
};
module.exports = { createSchedulingCore };
